<template>
  <div class="dp-root" :class="{ 'dp-open': isOpen, 'dp-disabled': disabled }" ref="root">
    <input
      :id="id || undefined"
      ref="input"
      type="text"
      class="dp-input"
      :class="[inputClass, { 'dp-input--placeholder': !modelValue }]"
      :value="displayText"
      :placeholder="placeholder"
      :disabled="disabled"
      readonly
      autocomplete="off"
      spellcheck="false"
      @click="toggle"
      @keydown.enter.prevent="toggle"
      @keydown.space.prevent="toggle"
      @keydown.down.prevent="open"
      @keydown.escape.prevent="close"
    />
    <button
      v-if="clearable && modelValue && !disabled"
      type="button"
      class="dp-clear"
      aria-label="Clear date"
      @mousedown.prevent
      @click.stop="clear"
    >&times;</button>
    <svg
      v-else
      class="dp-icon"
      width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </svg>

    <!-- Teleported so the calendar is never clipped by a filter toolbar's
         overflow or trapped under a sticky table header. `.admin-portal`
         re-declares the --g-* tokens, which do not reach outside .admin-page. -->
    <teleport to="body">
      <div
        v-if="isOpen"
        ref="pop"
        class="admin-portal dp-pop"
        :style="popStyle"
        role="dialog"
        aria-label="Choose date"
        @mousedown.stop
      >
        <div class="dp-head">
          <button type="button" class="dp-nav" aria-label="Previous month" @click="shiftMonth(-1)">
            <svg class="dp-chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <div class="dp-head__selects">
            <select class="dp-select" :value="viewMonth" @change="onMonthPick">
              <option v-for="(m, i) in MONTHS" :key="m" :value="i">{{ m }}</option>
            </select>
            <select class="dp-select" :value="viewYear" @change="onYearPick">
              <option v-for="y in yearChoices" :key="y" :value="y">{{ y }}</option>
            </select>
          </div>
          <button type="button" class="dp-nav" aria-label="Next month" @click="shiftMonth(1)">
            <svg class="dp-chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        </div>

        <div class="dp-dow">
          <span v-for="d in DOW" :key="d">{{ d }}</span>
        </div>

        <div class="dp-grid">
          <button
            v-for="cell in cells"
            :key="cell.key"
            type="button"
            class="dp-day"
            :class="{
              'dp-day--muted': !cell.inMonth,
              'dp-day--today': cell.isToday,
              'dp-day--selected': cell.isSelected,
            }"
            :disabled="cell.disabled"
            @click="pick(cell.iso)"
          >{{ cell.day }}</button>
        </div>

        <div class="dp-foot">
          <button type="button" class="dp-foot__btn" :disabled="todayDisabled" @click="pick(todayISO)">
            Today
          </button>
          <button type="button" class="dp-foot__btn dp-foot__btn--ghost" @click="clear">
            Clear
          </button>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
/**
 * DatePicker — the admin reports' date field.
 *
 * A drop-in replacement for `<input type="date" class="admin-input">`: the
 * v-model value is the same "YYYY-MM-DD" string, so every caller and
 * utils/dateRange.js keep working untouched. What changes is the calendar —
 * the native one is the browser's, so it ignored the console's type, spacing
 * and indigo accent, and looked different in every browser.
 *
 * Dates are handled by their Y/M/D parts and never through
 * `new Date('YYYY-MM-DD')` or `toISOString()`, both of which go via UTC and
 * shift the day by the viewer's offset (5h30m for the IST admins) — the same
 * trap documented at the top of utils/dateRange.js.
 */
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const SHORT_MONTHS = MONTHS.map((m) => m.slice(0, 3));
const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** "YYYY-MM-DD" -> {y, m, d} (m is 0-based), or null. */
function parseISO(value) {
  const match = DATE_ONLY.exec(String(value ?? "").trim());
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]) - 1;
  const d = Number(match[3]);
  // The Date constructor rolls "2026-13-45" over into a valid-but-wrong day
  // rather than failing, so verify the parts survived the round trip.
  const probe = new Date(y, m, d);
  if (probe.getFullYear() !== y || probe.getMonth() !== m || probe.getDate() !== d) return null;
  return { y, m, d };
}

/** {y, m, d} -> "YYYY-MM-DD". */
function toISO(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

export default {
  name: "DatePicker",
  props: {
    modelValue: { type: String, default: "" },
    /** Inclusive bounds, "YYYY-MM-DD". Days outside them are not clickable. */
    min: { type: String, default: "" },
    max: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    clearable: { type: Boolean, default: true },
    placeholder: { type: String, default: "Select date" },
    /** Set when a <label for="..."> points at this field. */
    id: { type: String, default: "" },
    /**
     * Class for the inner text field. Defaults to the console's `.admin-input`;
     * pages with their own field styling (DealsAdmin's `.da-input`) pass theirs,
     * since a class set on the component itself would land on the wrapper.
     */
    inputClass: { type: [String, Array, Object], default: "admin-input" },
  },
  emits: ["update:modelValue", "change"],
  data() {
    const now = new Date();
    return {
      MONTHS: SHORT_MONTHS,
      DOW,
      isOpen: false,
      viewYear: now.getFullYear(),
      viewMonth: now.getMonth(),
      popStyle: {},
    };
  },
  computed: {
    selected() {
      return parseISO(this.modelValue);
    },
    todayISO() {
      const n = new Date();
      return toISO(n.getFullYear(), n.getMonth(), n.getDate());
    },
    todayDisabled() {
      return this.isOutOfRange(this.todayISO);
    },
    displayText() {
      const s = this.selected;
      if (!s) return "";
      return `${s.d} ${SHORT_MONTHS[s.m]} ${s.y}`;
    },
    /** A window around the view, always wide enough to reach both bounds. */
    yearChoices() {
      const anchor = new Date().getFullYear();
      const lo = parseISO(this.min)?.y ?? anchor - 10;
      const hi = parseISO(this.max)?.y ?? anchor + 5;
      const from = Math.min(lo, hi, this.viewYear, anchor - 10);
      const to = Math.max(lo, hi, this.viewYear, anchor + 5);
      const out = [];
      for (let y = from; y <= to; y++) out.push(y);
      return out;
    },
    /** Always six rows, so the popover never changes height month to month. */
    cells() {
      const first = new Date(this.viewYear, this.viewMonth, 1);
      const start = new Date(this.viewYear, this.viewMonth, 1 - first.getDay());
      const out = [];
      for (let i = 0; i < 42; i++) {
        const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
        const iso = toISO(d.getFullYear(), d.getMonth(), d.getDate());
        out.push({
          key: iso,
          iso,
          day: d.getDate(),
          inMonth: d.getMonth() === this.viewMonth,
          isToday: iso === this.todayISO,
          isSelected: iso === this.modelValue,
          disabled: this.isOutOfRange(iso),
        });
      }
      return out;
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(v) {
        const s = parseISO(v);
        if (s) {
          this.viewYear = s.y;
          this.viewMonth = s.m;
        }
      },
    },
  },
  beforeUnmount() {
    this.detach();
  },
  methods: {
    // Lexicographic comparison is safe on zero-padded "YYYY-MM-DD".
    isOutOfRange(iso) {
      if (this.min && iso < this.min) return true;
      if (this.max && iso > this.max) return true;
      return false;
    },
    toggle() {
      if (this.disabled) return;
      this.isOpen ? this.close() : this.open();
    },
    open() {
      if (this.disabled || this.isOpen) return;
      // Land on the selected month, or on today's when the field is empty.
      const s = this.selected;
      const anchor = s || parseISO(this.todayISO);
      this.viewYear = anchor.y;
      this.viewMonth = anchor.m;
      this.isOpen = true;
      this.$nextTick(() => {
        this.position();
        this.attach();
      });
    },
    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.detach();
    },
    attach() {
      document.addEventListener("mousedown", this.onDocMouseDown);
      document.addEventListener("keydown", this.onDocKeydown);
      // `true` — catch scrolling in any ancestor. The app scrolls <body>, not
      // the window (see the note in CLAUDE.md), so a listener on window alone
      // would never fire and the popover would drift away from its field.
      window.addEventListener("scroll", this.position, true);
      window.addEventListener("resize", this.position);
    },
    detach() {
      document.removeEventListener("mousedown", this.onDocMouseDown);
      document.removeEventListener("keydown", this.onDocKeydown);
      window.removeEventListener("scroll", this.position, true);
      window.removeEventListener("resize", this.position);
    },
    onDocMouseDown(e) {
      const root = this.$refs.root;
      const pop = this.$refs.pop;
      if (root && root.contains(e.target)) return;
      if (pop && pop.contains(e.target)) return;
      this.close();
    },
    onDocKeydown(e) {
      if (e.key === "Escape") {
        this.close();
        this.$refs.input && this.$refs.input.focus();
      }
    },
    /**
     * Fixed positioning against the trigger's viewport rect. Deliberately not
     * page coordinates: the app scrolls <body>, so window.scrollY is always 0
     * and any scrollY-based maths would silently place the popover wrong.
     */
    position() {
      const root = this.$refs.root;
      const pop = this.$refs.pop;
      if (!root || !pop) return;
      const r = root.getBoundingClientRect();
      const w = pop.offsetWidth || 288;
      const h = pop.offsetHeight || 320;
      const gap = 6;
      const pad = 8;

      // Below by default; flip above when the bottom of the viewport is closer
      // than the popover is tall and there is more room up top.
      const below = r.bottom + gap;
      const flip = below + h > window.innerHeight - pad && r.top - gap - h > pad;
      const top = flip ? r.top - gap - h : below;

      // A short viewport can leave room on neither side; clamp so the calendar
      // is always fully visible (overlapping the field beats hanging off the
      // bottom edge, where the last rows and the Today/Clear buttons are lost).
      const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), Math.max(lo, hi));
      this.popStyle = {
        top: `${Math.round(clamp(top, pad, window.innerHeight - h - pad))}px`,
        left: `${Math.round(clamp(r.left, pad, window.innerWidth - w - pad))}px`,
      };
    },
    shiftMonth(delta) {
      const d = new Date(this.viewYear, this.viewMonth + delta, 1);
      this.viewYear = d.getFullYear();
      this.viewMonth = d.getMonth();
    },
    onMonthPick(e) {
      this.viewMonth = Number(e.target.value);
    },
    onYearPick(e) {
      this.viewYear = Number(e.target.value);
    },
    pick(iso) {
      if (this.isOutOfRange(iso)) return;
      this.$emit("update:modelValue", iso);
      this.$emit("change", iso);
      this.close();
    },
    clear() {
      this.$emit("update:modelValue", "");
      this.$emit("change", "");
      this.close();
    },
  },
};
</script>

<style scoped>
/* ── Trigger ─────────────────────────────────────────────────────────── */
.dp-root { position: relative; display: block; }
.dp-input {
  width: 100%;
  cursor: pointer;
  /* Room for the trailing icon so a long date never runs under it. */
  padding-right: 34px;
}
.dp-input--placeholder { color: var(--g-text-muted, #98a2b3); }
.dp-open .dp-input {
  border-color: var(--g-blue, #4f46e5);
  box-shadow: 0 0 0 3px var(--g-blue-tint, #eef2ff);
}
.dp-disabled .dp-input { cursor: not-allowed; }

.dp-icon,
.dp-clear {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  /* An icon alone in a flex/grid parent can collapse to zero width — pin it. */
  flex: 0 0 auto;
  pointer-events: none;
  color: var(--g-text-muted, #98a2b3);
}
.dp-clear {
  pointer-events: auto;
  border: none;
  background: none;
  padding: 0;
  width: 20px;
  height: 20px;
  line-height: 18px;
  font-size: 17px;
  border-radius: 50%;
  cursor: pointer;
}
.dp-clear:hover { background: var(--g-hover, #f6f7fb); color: var(--g-text, #111827); }
</style>

<!--
  The calendar panel is TELEPORTED to <body>, so its styles are deliberately
  NOT scoped. Scoped CSS relies on a data-v attribute reaching the element; for
  teleported content that is the fragile part of the setup, and if it does not
  land, every rule below is lost at once — the panel loses its width and grid
  and the day numbers spill out of the box. Every selector here is namespaced
  under .dp-pop, so nothing can leak onto the rest of the app.
-->
<style>

.dp-pop {
  position: fixed;
  z-index: 2147483001; /* above the teleported admin modals */
  width: 288px;
  padding: 10px;
  background: var(--g-surface, #fff);
  color: var(--g-text, #111827);
  border: 1px solid var(--g-border, #e5e7eb);
  border-radius: 14px;
  box-shadow: 0 12px 32px rgba(16, 24, 40, 0.16);
  /* Teleported to <body>, so `inherit` would pick up the document font rather
     than the console's. .admin-portal declares the token; name it explicitly. */
  font-family: var(--g-font);
  font-size: 13px;
}

.dp-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 8px;
}
.dp-head__selects { display: flex; gap: 2px; flex: 1 1 auto; min-width: 0; justify-content: center; }
.dp-select {
  /* Month and year read as the calendar's title, so they size to their text
     instead of stretching across the header like form fields. */
  flex: 0 1 auto;
  min-width: 0;
  height: 30px;
  padding: 0 2px;
  font: inherit;
  font-weight: 600;
  color: var(--g-text, #111827);
  background: transparent;
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: pointer;
}
.dp-select:hover { background: var(--g-hover, #f6f7fb); }
.dp-select:focus { outline: none; border-color: var(--g-blue, #4f46e5); }

.dp-nav {
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  background: none;
  color: var(--g-text-secondary, #5b6472);
  cursor: pointer;
}
.dp-nav:hover { background: var(--g-hover, #f6f7fb); color: var(--g-text, #111827); }

.dp-dow {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 2px;
}
.dp-dow span {
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--g-text-muted, #98a2b3);
  padding: 4px 0;
}

.dp-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; }
.dp-day {
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  background: none;
  font: inherit;
  color: var(--g-text, #111827);
  cursor: pointer;
  transition: background-color .12s, color .12s;
}
.dp-day:hover:not(:disabled) { background: var(--g-hover, #f6f7fb); }
.dp-day--muted { color: var(--g-text-muted, #98a2b3); }
.dp-day--today {
  font-weight: 700;
  box-shadow: inset 0 0 0 1px var(--g-blue-tint-2, #dcdefb);
}
.dp-day--selected,
.dp-day--selected:hover {
  background: var(--g-blue, #4f46e5);
  color: #fff;
  font-weight: 600;
  box-shadow: none;
}
.dp-day:disabled { color: #d3d8e0; cursor: not-allowed; }

.dp-foot {
  display: flex;
  gap: 6px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--g-border, #e5e7eb);
}
.dp-foot__btn {
  flex: 1 1 0;
  height: 30px;
  border: 1px solid var(--g-border, #e5e7eb);
  border-radius: 8px;
  background: var(--g-surface, #fff);
  font: inherit;
  font-weight: 600;
  color: var(--g-text, #111827);
  cursor: pointer;
}
.dp-foot__btn:hover:not(:disabled) { background: var(--g-hover, #f6f7fb); }
.dp-foot__btn:disabled { color: #d3d8e0; cursor: not-allowed; }
.dp-foot__btn--ghost { color: var(--g-text-secondary, #5b6472); }

/* ── Hardening ───────────────────────────────────────────────────────────
   The panel lands at <body>, outside .admin-page, so it inherits whatever
   global CSS (Bootstrap's reboot, the public site's rules) applies there.
   These pin the box model rather than trusting what it inherits. */
.dp-pop,
.dp-pop *,
.dp-pop *::before,
.dp-pop *::after { box-sizing: border-box; }

/* Never wider than the screen on a phone, and never shrink-to-fit. */
.dp-pop { max-width: calc(100vw - 16px); min-width: 288px; }

/* Bootstrap's reboot restyles buttons and selects; neutralise it inside the
   panel so the grid keeps its own metrics. */
.dp-pop button,
.dp-pop select {
  margin: 0;
  font-family: inherit;
  text-transform: none;
  appearance: none;
  -webkit-appearance: none;
}

/* An SVG alone in a flex parent can be collapsed to zero width (or, unsized,
   stretch and push the header past the panel edge) — pin both arrows. */
.dp-pop .dp-chev { flex: 0 0 auto; width: 14px; height: 14px; display: block; }

/* The 7-column grids must fill the panel exactly and never overflow it. */
.dp-pop .dp-dow,
.dp-pop .dp-grid { width: 100%; max-width: 100%; }
.dp-pop .dp-dow span,
.dp-pop .dp-day { min-width: 0; overflow: hidden; }
.dp-pop .dp-day { padding: 0; line-height: 1; }

/* Long month names must not stretch the header. The caret is drawn here
   because `appearance: none` above removes the native one, which would leave
   month and year looking like plain text rather than menus. */
.dp-pop .dp-select {
  max-width: 46%;
  text-overflow: ellipsis;
  padding-right: 16px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%235b6472' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 3px center;
}
</style>
